import React, { useState, useEffect, useCallback } from 'react';
import { getAdvisorDashboardData, getPosters, getPosterCategories, getImageUrl, API_BASE_URL } from '../model/api.js';
import LoadingSpinner from './components/common/LoadingSpinner.jsx';
import { GeneralInsuranceTab } from './components/GeneralInsuranceTab.jsx';
import { MutualFundTab } from './components/MutualFundTab.jsx';
import ProfileTab from './components/ProfileTab.jsx';
import { toast } from 'react-toastify';

const AdvisorDashboard = ({ user, refreshUser }) => {
  // Handle case where user is not available
  // Handle case where user is not available
  if (!user || !user.id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user information...</p>
        </div>
      </div>
    );
  }

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Filter Constants
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const endYear = Math.max(2036, currentYear + 10);
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const handleYearChange = (e) => {
    const [_, month] = selectedMonth.split('-');
    setSelectedMonth(`${e.target.value}-${month}`);
  };

  const handleMonthSelectChange = (e) => {
    const [year, _] = selectedMonth.split('-');
    setSelectedMonth(`${year}-${e.target.value}`);
  };
  const [activeTab, setActiveTab] = useState('dashboard');
  const [posters, setPosters] = useState([]);
  const [posterCategories, setPosterCategories] = useState([]);
  const [posterLoading, setPosterLoading] = useState(false);
  const [selectedPosterCategory, setSelectedPosterCategory] = useState('all');

  const [posterViewMode, setPosterViewMode] = useState('grid');
  const [downloadingPosterId, setDownloadingPosterId] = useState(null);


  const fetchData = useCallback(async (month = selectedMonth) => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [year, monthNum] = month.split('-');
      const result = await getAdvisorDashboardData(user.id, parseInt(monthNum), parseInt(year));
      setData(result);
    } catch (error) {
      console.error("Failed to fetch advisor data", error);
      setData({
        monthlyRevenue: 0,
        activationThreshold: 0,
        activationStatus: false,
        commission: 0,
        policiesCount: 0,
        recentPolicies: [],
        monthlyBreakdown: [],
        currentMonth: selectedMonth,
        totalPolicies: 0
      });
    } finally {
      setLoading(false);
    }
  }, [user, selectedMonth]);

  const fetchPosters = useCallback(async () => {
    setPosterLoading(true);
    try {
      const params = {
        category: selectedPosterCategory === 'all' ? undefined : selectedPosterCategory,

        limit: 50
      };



      const result = await getPosters(params);



      if (result.ok && result.data) {
        setPosters(result.data.posters || []);

        // Show message if user doesn't have access to category
        if (result.message && result.data.posters.length === 0) {
          toast.info(result.message);
        }
      } else {
        setPosters([]);
      }
    } catch (error) {
      console.error('❌ Error fetching posters:', error);
      setPosters([]);
      toast.error('Failed to fetch posters');
    } finally {
      setPosterLoading(false);
    }
  }, [selectedPosterCategory]);

  const fetchPosterCategories = useCallback(async () => {
    try {
      const result = await getPosterCategories();
      if (result.ok && result.data) {
        setPosterCategories(result.data);
      } else {
        setPosterCategories([]);
      }
    } catch (error) {
      console.error('Error fetching poster categories:', error);
      setPosterCategories([]);
    }
  }, []);

  const isNewPoster = (createdAt) => {
    if (!createdAt) return false;
    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffInHours = (now - createdDate) / (1000 * 60 * 60);
    return diffInHours <= 168; // 7 days (7 * 24 = 168 hours)
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'posters') {
      fetchPosters();
      fetchPosterCategories();
    }
  }, [activeTab, fetchPosters, fetchPosterCategories]);

  const downloadPersonalizedPoster = async (posterId, posterName, userName) => {
    setDownloadingPosterId(posterId);
    try {
      // Get token from cookies
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };

      const token = getCookie('token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/posters/${posterId}/download-personalized`, {
        headers
      });

      if (!response.ok) {
        // Safely parse error - handle both JSON and non-JSON responses
        let errorMessage = `Failed to download poster (${response.status})`;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (parseErr) {
          console.warn('Could not parse error response:', parseErr);
        }
        throw new Error(errorMessage);
      }

      // Get the blob
      const blob = await response.blob();

      // Verify blob size
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Check if it's actually JSON error response
      if (blob.type === 'application/json' || blob.type.includes('json')) {
        const text = await blob.text();
        let errorMessage = 'Server returned an error';
        try {
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Verify it's actually a JPEG by checking magic bytes
      const arrayBuffer = await blob.slice(0, 2).arrayBuffer();
      const view = new Uint8Array(arrayBuffer);



      if (view[0] !== 0xFF || view[1] !== 0xD8) {
        // Try to read as text to see if it's an error message
        const text = await blob.text();
        if (text.includes('error') || text.includes('Error')) {
          throw new Error(text.substring(0, 200));
        }
        throw new Error(`Downloaded file is not a valid JPEG image (got bytes: 0x${view[0].toString(16)} 0x${view[1].toString(16)})`);
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${posterName}_${userName}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Poster downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Download failed: ${error.message}`);
    } finally {
      setDownloadingPosterId(null);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!data) {
    return <div className="text-center text-red-500">Failed to load dashboard data.</div>;
  }

  const progressPercentage = Math.min((data.monthlyRevenue / data.activationThreshold) * 100, 100);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            {/* Month and Year Selector */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Filter</h3>
                <div className="flex gap-4">
                  <div>
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      value={selectedMonth.split('-')[1]}
                      onChange={handleMonthSelectChange}
                    >
                      {months.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      value={selectedMonth.split('-')[0]}
                      onChange={handleYearChange}
                    >
                      {years.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md mb-10">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                Your Performance Summary - {data?.currentMonth || selectedMonth}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="text-center">
                  <p className="text-lg text-gray-500">Total Revenue (This Month)</p>
                  <p className="text-5xl font-bold text-indigo-600 my-2">₹{(data.monthlyRevenue || 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="text-center">
                  <p className="text-lg text-gray-500">Activation Status</p>
                  {data.activationStatus ? (
                    <div className="flex items-center justify-center mt-2">
                      <CheckCircleIcon className="h-12 w-12 text-green-500 mr-2" />
                      <p className="text-4xl font-bold text-green-500">Active</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center mt-2">
                      <XCircleIcon className="h-12 w-12 text-red-500 mr-2" />
                      <p className="text-4xl font-bold text-red-500">Inactive</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-md font-medium text-gray-700">Progress to Activation</h4>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>₹{(data.monthlyRevenue || 0).toLocaleString('en-IN')}</span>
                  <span>Target: ₹{(data.activationThreshold || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-green-500 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}>
                  </div>
                </div>
                {!data.activationStatus && (
                  <p className="text-center mt-2 text-sm text-gray-500">
                    You need ₹{((data.activationThreshold || 0) - (data.monthlyRevenue || 0)).toLocaleString('en-IN')} more to become active.
                  </p>
                )}
              </div>
            </div>

            {/* Policies Section */}
            <div className="bg-white p-8 rounded-lg shadow-md mb-10">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                Policies This Month ({data?.policiesCount || 0} policies)
              </h3>

              {data?.recentPolicies && data.recentPolicies.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {data.recentPolicies.map((policy, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-gray-900">{policy.policyNo}</h4>
                          <span className="text-sm text-gray-500">{policy.productName}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{policy.customerName}</p>
                        <p className="text-lg font-semibold text-indigo-600">₹{(policy.premium || 0).toLocaleString('en-IN')}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(policy.startDate).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No policies found for {data?.currentMonth || selectedMonth}</p>
                </div>
              )}
            </div>

            {/* Monthly Breakdown */}
            {data?.monthlyBreakdown && data.monthlyBreakdown.length > 0 && (
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">
                  Your Performance History
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policies</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.monthlyBreakdown.slice(0, 6).map((month, index) => (
                        <tr key={index} className={month.month === data.currentMonth ? 'bg-indigo-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {month.month}
                            {month.month === data.currentMonth && <span className="ml-2 text-xs text-indigo-600">(Current)</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{month.policiesCount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{(month.revenue || 0).toLocaleString('en-IN')}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${month.revenue >= data.activationThreshold
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                              }`}>
                              {month.revenue >= data.activationThreshold ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      case 'general':
        return <GeneralInsuranceTab userRole="advisor" user={user} />;
      case 'posters':
        return (
          <div className="space-y-6">
            {/* Filter */}
            <div className="flex justify-end">
              <div className="w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Category</label>
                <select
                  value={selectedPosterCategory}
                  onChange={(e) => setSelectedPosterCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Categories</option>
                  {posterCategories.map(category => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Content */}
            {posterLoading ? (
              <LoadingSpinner />
            ) : posterViewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {posters.map(poster => (
                  <div key={poster._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="aspect-w-3 aspect-h-4 bg-gray-200 relative">
                      <img
                        src={`${API_BASE_URL}/posters/${poster._id}/image`}
                        alt={poster.name}
                        className="w-full h-48 object-cover cursor-pointer hover:opacity-80"
                        onClick={() => window.open(`${API_BASE_URL}/posters/${poster._id}/image`, '_blank')}
                      />
                      {isNewPoster(poster.createdAt) && (
                        <span className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm z-10">
                          NEW
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadPersonalizedPoster(poster._id, poster.name, user.name);
                        }}
                        className="absolute top-2 right-2 bg-indigo-600 text-white p-2 rounded-full hover:bg-indigo-700 transition-colors shadow-lg flex items-center justify-center"
                        title="Download with your details"
                      >
                        {downloadingPosterId === poster._id ? (
                          <div className="animate-spin">
                            <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                        ) : (
                          <DownloadIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1">{poster.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{poster.description || 'No description'}</p>
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium`} style={{ backgroundColor: `${poster.category?.color}20`, color: poster.category?.color }}>
                          {poster.category?.name || 'N/A'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {(poster.file_size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Upload Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Size</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {posters.map(poster => (
                        <tr key={poster._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="relative inline-block">
                              <img
                                src={`${API_BASE_URL}/posters/${poster._id}/image`}
                                alt={poster.name}
                                className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
                                onClick={() => window.open(`${API_BASE_URL}/posters/${poster._id}/image`, '_blank')}
                              />
                              {isNewPoster(poster.createdAt) && (
                                <span className="absolute top-0 left-0 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-br rounded-tl leading-none shadow-sm">
                                  NEW
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{poster.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium`} style={{ backgroundColor: `${poster.category?.color}20`, color: poster.category?.color }}>
                              {poster.category?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{poster.description || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{poster.uploaded_by?.name || 'Unknown'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(poster.createdAt).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(poster.file_size / 1024).toFixed(1)} KB</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => downloadPersonalizedPoster(poster._id, poster.name, user.name)}
                              disabled={downloadingPosterId === poster._id}
                              className="text-indigo-600 hover:text-indigo-900 inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Download with your details"
                            >
                              {downloadingPosterId === poster._id ? (
                                <svg className="w-4 h-4 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <DownloadIcon className="w-4 h-4 mr-1" />
                              )}
                              {downloadingPosterId === poster._id ? 'Downloading...' : 'Download'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {posters.length === 0 && !posterLoading && (
              <div className="text-center py-8">
                <p className="text-gray-500">No posters found matching your criteria.</p>
              </div>
            )}
          </div>
        );
      case 'mutual-fund':
        return <MutualFundTab userRole="advisor" userId={user.id} />;
      case 'profile':
        return <ProfileTab user={user} onUpdate={refreshUser} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-900">Welcome, {user.name}</h2>

      {/* Navigation Tabs */}
      <nav className="bg-white p-2 rounded-lg shadow-md flex space-x-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${activeTab === 'dashboard'
            ? 'bg-indigo-600 text-white shadow'
            : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
          Health Insurance
        </button>
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${activeTab === 'general'
            ? 'bg-indigo-600 text-white shadow'
            : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
          General Insurance
        </button>
        <button
          onClick={() => setActiveTab('posters')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${activeTab === 'posters'
            ? 'bg-indigo-600 text-white shadow'
            : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
          Marketing Posters
        </button>
        <button
          onClick={() => setActiveTab('mutual-fund')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${activeTab === 'mutual-fund'
            ? 'bg-indigo-600 text-white shadow'
            : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
          Mutual Funds
        </button>
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${activeTab === 'profile'
            ? 'bg-indigo-600 text-white shadow'
            : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
          My Profile
        </button>
      </nav>

      <div className="mt-6">
        {renderContent()}
      </div>
    </div>
  );
};

const CheckCircleIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const XCircleIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const GridIcon = ({ className }) => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path></svg>;
const TableIcon = ({ className }) => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>;
const DownloadIcon = ({ className }) => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>;

export default AdvisorDashboard;